import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Users, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Separator } from './ui/separator';
import DatePicker from './DatePicker';
import ParkingAddOn from './ParkingAddOn';
import GuestPicker from './GuestPicker';
import PaymentModal from './PaymentModal';
import RequestToBookModal from './RequestToBookModal';
import PackageSelector from './PackageSelector';
import IdVerificationRequiredModal from './IdVerificationRequiredModal';
import AccountAppealModal from './AccountAppealModal';
import PromoCodeInput from './PromoCodeInput';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { BACKEND_URL, FEATURE_FLAGS } from '../config'  // Auto-fixed: use centralized config;
import OTABookPanel from './OTABookPanel';
import usePropertyPricing from '../hooks/usePropertyPricing';
import useAvailabilityCalendar from '../hooks/useAvailabilityCalendar';
const API_BASE = `${BACKEND_URL}/api`;

const BookingWidget = ({ 
  property, 
  bookingSource = 'vacation',  // Default to vacation if not provided
  prefilledCheckIn,
  prefilledCheckOut,
  prefilledAdults,
  prefilledChildren,
  onDatesChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const API_BASE = BACKEND_URL;
  
  // CRITICAL FIX: Detect booking source from URL if not provided or is default
  const detectedBookingSource = (() => {
    if (bookingSource && bookingSource !== 'vacation') return bookingSource;
    
    // Fallback: Check URL for source parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlSource = urlParams.get('source');
    if (urlSource) return urlSource;
    
    // Final fallback: Check URL path
    const path = window.location.pathname;
    if (path.includes('/students')) return 'students';
    if (path.includes('/medical')) return 'medical';
    if (path.includes('/insurance')) return 'insurance';
    
    return 'vacation'; // Default
  })();
  
  console.log('🔍 BOOKING SOURCE DETECTION:', {
    propValue: bookingSource,
    detectedValue: detectedBookingSource,
    urlPath: window.location.pathname,
    urlSearch: window.location.search
  });
  
  const [checkInDate, setCheckInDate] = useState(prefilledCheckIn);
  const [checkOutDate, setCheckOutDate] = useState(prefilledCheckOut);
  const [guests, setGuests] = useState({ 
    adults: prefilledAdults || 1, 
    children: prefilledChildren || 0 
  });

  // Parking add-on — owned here so the total can factor it in and the
  // payment payload can include the car size the guest chose. The
  // ParkingAddOn child handles the live availability query.
  const [parkingSelection, setParkingSelection] = useState({ requested: false });
  const handleParkingChange = useCallback((next) => setParkingSelection(next), []);
  const parkingAddTotal = parkingSelection?.available && parkingSelection?.priceBreakdown
    ? Number(parkingSelection.priceBreakdown.total) || 0
    : 0;
  
  // Check if room booking is available FIRST (before state initialization)
  const hasRoomsAvailable = property?.rooms && property.rooms.length > 0;
  const hasLegacyRoomPrice = property?.bedrooms >= 2 && property?.room_price;
  const isStudentOrMedicalSection = detectedBookingSource === 'students' || detectedBookingSource === 'medical';
  const isRoomBookingAvailable = isStudentOrMedicalSection && (hasRoomsAvailable || hasLegacyRoomPrice);
  
  console.log('🏠 ROOM BOOKING AVAILABILITY CHECK:', {
    hasRoomsAvailable,
    roomsCount: property?.rooms?.length || 0,
    hasLegacyRoomPrice,
    isStudentOrMedicalSection,
    isRoomBookingAvailable,
    detectedBookingSource
  });
  
  // Initialize booking type based on whether room booking is available
  const [bookingType, setBookingType] = useState(
    isRoomBookingAvailable && hasRoomsAvailable ? 'single_room' : 'full_property'
  );
  const [selectedRoom, setSelectedRoom] = useState(
    isRoomBookingAvailable && hasRoomsAvailable ? property.rooms[0] : null
  );
  
  console.log('🎯 INITIAL STATE:', {
    bookingType,
    selectedRoom: selectedRoom ? { 
      name: selectedRoom.name, 
      room_number: selectedRoom.room_number,
      price: selectedRoom.price 
    } : null
  });
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  // Promo code applied at this widget — server re-validates before charging.
  const [appliedPromo, setAppliedPromo] = useState(null);

  // Availability + calendar-blocking is fully encapsulated in this hook.
  const {
    isDateUnavailable,
    isDateUnavailableForCheckout,
  } = useAvailabilityCalendar({
    property,
    checkInDate,
    bookingType,
    selectedRoom,
  });

  // Check if packages are available
  const hasPackagesAvailable = hasRoomsAvailable && property.rooms.some(room => 
    room.package_deals && room.package_deals.some(pkg => pkg.enabled)
  );


  // CRITICAL: Ensure room is selected when in single_room mode
  useEffect(() => {
    if (bookingType === 'single_room' && hasRoomsAvailable && !selectedRoom) {
      console.log('⚠️ single_room mode but no room selected! Auto-selecting first room...');
      setSelectedRoom(property.rooms[0]);
      console.log('✅ Auto-selected room:', property.rooms[0].name);
    }
  }, [bookingType, hasRoomsAvailable, selectedRoom, property]);

  // Auto-calculate check-out date when package is selected and check-in date is chosen
  useEffect(() => {
    if (selectedPackage && checkInDate) {
      // Calculate check-out date based on package duration (in months)
      const checkOutCalculated = new Date(checkInDate);
      checkOutCalculated.setMonth(checkOutCalculated.getMonth() + selectedPackage.duration_months);
      setCheckOutDate(checkOutCalculated);

      console.log(`📦 Auto-calculated check-out date: ${checkOutCalculated.toISOString().split('T')[0]} (${selectedPackage.duration_months} months from ${checkInDate.toISOString().split('T')[0]})`);
    }
  }, [selectedPackage, checkInDate]);

  // Bubble up the selected dates so parents (e.g. PropertyDetailPage) can
  // display them on the mobile sticky reserve bar without duplicating state.
  useEffect(() => {
    if (typeof onDatesChange === 'function') {
      onDatesChange({ checkIn: checkInDate || null, checkOut: checkOutDate || null });
    }
  }, [checkInDate, checkOutDate, onDatesChange]);

  // Pricing math lives in usePropertyPricing — returns the totals object or null.
  // promoDiscount is folded into the accommodation before taxes so the
  // displayed total matches what the backend (`payments.py`) charges Stripe.
  const totals = usePropertyPricing({
    property,
    checkInDate,
    checkOutDate,
    guests,
    bookingType,
    selectedRoom,
    selectedPackage,
    promoDiscount: appliedPromo?.savings || 0,
  });

  const openBookingModal = useCallback(() => {
    if (property.booking_mode === 'request') {
      setShowRequestModal(true);
    } else {
      setShowPaymentModal(true);
    }
  }, [property.booking_mode]);

  const checkVerificationAndProceed = useCallback(async () => {
    const userToken = localStorage.getItem('user_token');
    if (!userToken) {
      openBookingModal();
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/verification/status`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      if (!res.ok) {
        openBookingModal();
        return;
      }
      const data = await res.json();
      if (data?.id_verified) {
        openBookingModal();
      } else {
        setShowVerificationModal(true);
      }
    } catch {
      openBookingModal();
    }
  }, [openBookingModal]);

  const handleReserve = async () => {
    // CHECK 1: User must be logged in to book
    const userToken = localStorage.getItem('user_token');
    if (!userToken) {
      toast({
        title: 'Account Required',
        description: 'Please create an account or log in to book a property.',
        variant: 'destructive',
      });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    if (!checkInDate) {
      toast({
        title: 'Missing information',
        description: selectedPackage ? 'Please select a move-in date' : 'Please select check-in and check-out dates',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedPackage && !checkOutDate) {
      toast({
        title: 'Missing information',
        description: 'Please select check-out date',
        variant: 'destructive',
      });
      return;
    }

    // CHECK: Validate minimum nights requirement
    if (!selectedPackage && checkInDate && checkOutDate) {
      const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      const minimumNights = property.minimum_nights || 1;
      if (nights < minimumNights) {
        toast({
          title: 'Minimum stay requirement not met',
          description: `This property requires a minimum stay of ${minimumNights} ${minimumNights === 1 ? 'night' : 'nights'}. You selected ${nights} ${nights === 1 ? 'night' : 'nights'}. Please adjust your dates.`,
          variant: 'destructive',
        });
        return;
      }
    }

    // CHECK: Stripe Identity gate — must be verified before first booking.
    // If the user isn't verified, open the inline verification modal; once
    // verified we reopen the booking modal automatically via onVerified.
    await checkVerificationAndProceed();
  };

  const handleRequestSubmit = async (requestData) => {
    // Prevent double submission
    if (window.bookingInProgress) {
      console.log('⚠️ Booking already in progress, ignoring duplicate submission');
      return;
    }
    
    try {
      window.bookingInProgress = true;  // Set global flag
      const userToken = localStorage.getItem('user_token') || localStorage.getItem('token');
      
      // The RequestToBookModal now handles payment authorization
      // and passes the payment_intent_id in requestData
      const response = await axios.post(
        `${API_BASE}/booking-requests/create`,
        {
          property_id: property.id || property._id,
          check_in: checkInDate.toISOString().split('T')[0],
          check_out: selectedPackage 
            ? new Date(checkInDate.getTime() + selectedPackage.duration_months * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            : checkOutDate.toISOString().split('T')[0],
          guests: guests.adults + guests.children,
          customer_name: requestData.name,
          customer_email: requestData.email,
          customer_phone: requestData.phone,
          message: requestData.message || '',
          payment_intent_id: requestData.payment_intent_id, // NEW: Payment authorization ID
          booking_type: bookingType,  // "full_property" or "single_room"
          room_number: selectedRoom?.room_number,  // DEPRECATED: backward compatibility
          room_id: selectedRoom?.id,  // NEW: Room ID
          room_name: selectedRoom?.name,  // NEW: Room name for display
          is_package: !!selectedPackage,
          package_duration: selectedPackage?.duration_months
        },
        {
          headers: userToken ? { Authorization: `Bearer ${userToken}` } : {},
        }
      );
      
      console.log('✅ Request to Book submitted successfully:', response.data);
      
      // Close modal first
      setShowRequestModal(false);
      
      // Show success toast
      toast({
        title: 'Request Sent Successfully! 🎉',
        description: 'Your card has been authorized (not charged). The host will review your request and you\'ll receive an email when it\'s approved.',
        duration: 5000, // Show for 5 seconds
      });
      
      // Redirect to homepage after delay
      setTimeout(() => {
        console.log('Redirecting to homepage...');
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('❌ Request to Book error:', error);
      const detail = error?.response?.data?.detail;
      // Intercept ID verification gate
      if (error?.response?.status === 403 && detail?.error_code === 'id_verification_required') {
        setShowRequestModal(false);
        setShowVerificationModal(true);
      } else if (error?.response?.status === 403 && detail?.error_code === 'account_removed') {
        setShowRequestModal(false);
        toast({
          title: 'Account restricted',
          description: detail?.message || 'Your account is currently restricted from booking.',
          variant: 'destructive',
          duration: 6000,
          action: (
            <button
              type="button"
              onClick={() => setShowAppealModal(true)}
              className="text-xs font-semibold underline text-white"
              data-testid="open-appeal-from-toast"
            >
              Submit appeal
            </button>
          ),
        });
        // Also open the appeal modal right away so the user can act.
        setShowAppealModal(true);
      } else {
        toast({
          title: 'Error',
          description: typeof detail === 'string' ? detail : (detail?.message || 'Failed to send booking request'),
          variant: 'destructive',
        });
      }
      // Don't close modal on error - let user try again
    } finally {
      // Clear flag after 3 seconds to allow retry if needed
      setTimeout(() => {
        window.bookingInProgress = false;
      }, 3000);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setShowPaymentModal(false);
    
    // Check if this is a request booking or instant booking
    const isRequestBooking = property.booking_mode === 'request';
    
    toast({
      title: isRequestBooking ? 'Request submitted!' : 'Booking confirmed!',
      description: selectedPackage 
        ? `Your ${selectedPackage.duration_months}-month package ${isRequestBooking ? 'request has been submitted' : 'is confirmed'}!`
        : isRequestBooking
          ? 'Your booking request has been sent to the host. You will be notified once they approve!'
          : 'Your payment was successful. Thank you for your booking!',
    });
    setTimeout(() => navigate('/'), 3000);
  };

  // CRITICAL FIX: Make totals reactive to guest changes
  
  const totalGuests = guests.adults + guests.children;

  // Get display price
  const getDisplayPrice = () => {
    if (selectedPackage) {
      return `${selectedPackage.total_price.toLocaleString()}`;
    }
    if (bookingType === 'single_room') {
      if (selectedRoom) return selectedRoom.price;
      if (property.room_price) return property.room_price;
    }
    return property.price;
  };

  return (
    <Card className="sticky top-24 p-6 shadow-xl border-gray-200">
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold">
            CA${getDisplayPrice()}
          </span>
          <span className="text-gray-600">
            {selectedPackage ? `(${selectedPackage.duration_months}-mo package)` : 'night'}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-2">
          <span className="font-semibold">{property.rating}</span>
          <span className="text-gray-600 text-sm">· {property.reviews_count} reviews</span>
        </div>
        {property.minimum_nights && property.minimum_nights > 1 && (
          <div className="mt-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700 font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Minimum stay: {property.minimum_nights} nights
            </p>
          </div>
        )}
      </div>

      {/* Booking Type Selection */}
      {isRoomBookingAvailable && (
        <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <p className="text-sm font-semibold text-gray-700 mb-3">{t('property.selectRoom')}:</p>
          <div className="space-y-2">
            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors bg-white">
              <input
                type="radio"
                name="bookingType"
                value="full_property"
                checked={bookingType === 'full_property'}
                onChange={(e) => {
                  console.log('🔄 User switched to full_property mode');
                  setBookingType(e.target.value);
                  setSelectedRoom(null);
                  setSelectedPackage(null);
                }}
                className="w-4 h-4 mr-3 accent-[#1F5C42]"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">{t('property.wholeProperty')}</span>
                <p className="text-sm text-gray-600">CA${property.price}/{t('common.night')}</p>
              </div>
            </label>
            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors bg-white">
              <input
                type="radio"
                name="bookingType"
                value="single_room"
                checked={bookingType === 'single_room'}
                onChange={(e) => {
                  console.log('🔄 User switched to single_room mode');
                  setBookingType(e.target.value);
                  if (hasRoomsAvailable && !selectedRoom) {
                    setSelectedRoom(property.rooms[0]);
                  }
                  setSelectedPackage(null);
                }}
                className="w-4 h-4 mr-3 accent-[#1F5C42]"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">{t('property.bookRoomOnly')}</span>
                <p className="text-sm text-gray-600">
                  {hasRoomsAvailable 
                    ? `From CA$${Math.min(...property.rooms.map(r => r.price))}/${t('common.night')}`
                    : `CA$${property.room_price}/${t('common.night')}`}
                </p>
                {hasPackagesAvailable && (
                  <p className="text-xs text-blue-600 mt-1">📦 Long-term packages available</p>
                )}
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Room Selection */}
      {bookingType === 'single_room' && hasRoomsAvailable && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t('property.selectRoom')}
          </label>
          <select
            value={selectedRoom?.room_number || ''}
            onChange={(e) => {
              const room = property.rooms.find(r => r.room_number === parseInt(e.target.value));
              setSelectedRoom(room);
              setSelectedPackage(null);
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500"
          >
            {property.rooms.map(room => (
              <option key={room.room_number} value={room.room_number}>
                {room.name || `Room ${room.room_number}`} - CA${room.price}/night
                {room.amenities?.length > 0 && ` • ${room.amenities.slice(0, 2).join(', ')}`}
              </option>
            ))}
          </select>
          {selectedRoom?.description && (
            <p className="text-xs text-gray-600 mt-1">{selectedRoom.description}</p>
          )}
        </div>
      )}

      {/* Package Selection */}
      {bookingType === 'single_room' && selectedRoom?.package_deals?.length > 0 && (
        <div className="mb-4">
          <PackageSelector
            packages={selectedRoom.package_deals}
            selectedPackage={selectedPackage}
            onSelectPackage={(pkg) => {
              setSelectedPackage(pkg);
              if (pkg) {
                setCheckInDate(null);
                setCheckOutDate(null);
              }
            }}
            roomPrice={selectedRoom.price}
          />
        </div>
      )}

      <Separator className="my-4" />

      {/* Package Move-in Date */}
      {selectedPackage && (
        <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
          <h4 className="font-semibold text-green-900 mb-3">
            📦 {selectedPackage.duration_months}-Month Package
          </h4>
          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-gray-700">Total:</span>
              <span className="font-bold text-green-700">CA${selectedPackage.total_price.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Monthly Payment:</span>
              <span className="font-semibold text-green-700">CA${selectedPackage.monthly_payment}/mo</span>
            </div>
            <div className="text-xs text-green-600">
              ✓ Utilities & weekly cleaning included
            </div>
          </div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Move-in Date (Check-in)
          </label>
          <DatePicker
            date={checkInDate}
            onDateChange={setCheckInDate}
            placeholder="Select move-in date"
            isDateUnavailable={isDateUnavailable}
            compact
            hideLabel
          />
          
          {/* Auto-calculated Check-out Date Display */}
          {checkInDate && checkOutDate && (
            <div className="mt-3 p-3 bg-white border border-green-200 rounded-lg">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Move-out Date (Check-out) - Auto-calculated
              </label>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-gray-900">
                  {checkOutDate.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPackage.duration_months} months from your move-in date
              </p>
            </div>
          )}
        </div>
      )}

      {/* Regular Date Selection */}
      {!selectedPackage && (
        <div className="border border-gray-300 rounded-lg mb-4 overflow-hidden">
          <div className="grid grid-cols-2">
            <div className="p-3 border-r border-b border-gray-300">
              <label className="text-xs font-semibold block mb-1">{t('search.checkIn').toUpperCase()}</label>
              <DatePicker
                date={checkInDate}
                onDateChange={setCheckInDate}
                placeholder={t('search.addDates')}
                isDateUnavailable={isDateUnavailable}
                rangeStart={checkInDate}
                rangeEnd={checkOutDate}
                compact
                hideLabel
              />
            </div>
            <div className="p-3 border-b border-gray-300">
              <label className="text-xs font-semibold block mb-1">{t('search.checkOut').toUpperCase()}</label>
              <DatePicker
                date={checkOutDate}
                onDateChange={setCheckOutDate}
                placeholder={t('search.addDates')}
                isDateUnavailable={isDateUnavailableForCheckout}
                minDate={checkInDate}
                rangeStart={checkInDate}
                rangeEnd={checkOutDate}
                compact
                hideLabel
              />
            </div>
          </div>
          <div className="p-3" data-testid="guest-picker-container">
            <label className="text-xs font-semibold block mb-1">{t('common.guests').toUpperCase()}</label>
            <GuestPicker 
              guests={guests} 
              onGuestsChange={setGuests}
              maxGuests={bookingType === 'single_room' ? 1 : (property?.guests || 16)}
            />
            {bookingType === 'single_room' && (
              <p className="text-xs text-orange-600 mt-2">
                ℹ️ Single room bookings are limited to 1 guest
              </p>
            )}
            {/* Breakfast portions notice — free breakfast is one of our
                Montreal perks. The portion count is matched to the guest
                count, so we gently remind guests to set it correctly. */}
            <div
              className="mt-2 flex items-start gap-1.5 text-[11px] rounded-md px-2 py-1.5"
              style={{
                backgroundColor: 'rgba(255, 201, 60, 0.15)',
                color: '#7A4F00',
              }}
              data-testid="breakfast-portion-notice"
            >
              <span className="leading-none">🥐</span>
              <span className="leading-snug">
                Heads up — your <strong>free breakfast</strong> portions are based on the number of guests above. Please set the right count before checkout so we prep enough for everyone.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Policy Warning */}
      {property?.cancellation_policy && (
        <div className={`mb-4 border rounded-lg p-3 text-xs ${
          property.cancellation_policy === 'non_refundable' 
            ? 'bg-red-50 border-red-200' 
            : property.cancellation_policy === 'strict'
              ? 'bg-orange-50 border-orange-200'
              : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-start gap-2">
            <span className="text-base flex-shrink-0">
              {property.cancellation_policy === 'non_refundable' && '🚫'}
              {property.cancellation_policy === 'strict' && '🔒'}
              {property.cancellation_policy === 'moderate' && '⚖️'}
              {property.cancellation_policy === 'flexible' && 'ℹ️'}
            </span>
            <div className="flex-1">
              <p className={`font-semibold mb-1 ${
                property.cancellation_policy === 'non_refundable' 
                  ? 'text-red-900' 
                  : property.cancellation_policy === 'strict'
                    ? 'text-orange-900'
                    : 'text-blue-900'
              }`}>
                Cancellation Policy: {property.cancellation_policy === 'non_refundable' ? 'Non-Refundable' : property.cancellation_policy.charAt(0).toUpperCase() + property.cancellation_policy.slice(1)}
              </p>
              <p className={`${
                property.cancellation_policy === 'non_refundable' 
                  ? 'text-red-800' 
                  : property.cancellation_policy === 'strict'
                    ? 'text-orange-800'
                    : 'text-blue-800'
              }`}>
                {property.cancellation_policy === 'flexible' && 'Free cancellation up to 24 hours before check-in'}
                {property.cancellation_policy === 'moderate' && 'Free cancellation up to 5 days before check-in'}
                {property.cancellation_policy === 'strict' && 'Free cancellation up to 14 days before check-in'}
                {property.cancellation_policy === 'non_refundable' && 'This booking is non-refundable. No cancellations or refunds allowed.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Parking add-on — only renders when the property offers parking.
          Backend's `has_parking` flag is true when the listing has its own
          slots OR is attached to a shared ParkingPool with slots. The
          ParkingAddOn child fetches live availability from its own
          /parking-availability endpoint (slot inventory stays private). */}
      {checkInDate && checkOutDate && property?.has_parking && (
        <ParkingAddOn
          propertyId={property.id || property._id}
          checkIn={checkInDate}
          checkOut={checkOutDate}
          hasParking
          onSelectionChange={handleParkingChange}
        />
      )}

      {/* Reserve Button — OTA-referral model routes booking to partner sites */}
      {FEATURE_FLAGS.directBooking ? (
        <>
          <Button
            onClick={handleReserve}
            disabled={
              parkingSelection.requested && parkingSelection.available === null
                ? true  // still checking parking availability — don't let guest click Reserve yet
                : selectedPackage
                  ? !checkInDate
                  : (!checkInDate || !checkOutDate)
            }
            className="w-full text-white h-12 text-base font-semibold hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#1F5C42' }}
            data-testid="booking-widget-reserve"
          >
            {selectedPackage 
              ? `${t('common.confirm')} (${selectedPackage.duration_months} ${t('common.nights')})`
              : (property.booking_mode === 'request' ? t('property.requestToBook') : t('booking.reserveNow'))}
          </Button>

          <p className="text-center text-sm text-gray-600 mt-4">
            {property.booking_mode === 'request' 
              ? t('booking.requestToBookDesc')
              : t('booking.youWontBeCharged')}
          </p>
        </>
      ) : (
        <OTABookPanel
          city={property.city || property.location || 'Montreal'}
          checkIn={checkInDate}
          checkOut={checkOutDate}
          adults={totalGuests || 2}
        />
      )}

      {/* Price Breakdown */}
      {totals && (
        <>
          <Separator className="my-6" />

          {selectedPackage ? (
            <div className="space-y-3 text-sm">
              {/* Dates Summary */}
              {checkInDate && checkOutDate && (
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Check-in:</span>
                    <span className="font-semibold text-gray-900">
                      {checkInDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Check-out:</span>
                    <span className="font-semibold text-gray-900">
                      {checkOutDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                    <span className="text-gray-600 font-medium">Duration:</span>
                    <span className="font-semibold text-green-700">
                      {selectedPackage.duration_months} months
                    </span>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="underline">{selectedPackage.duration_months}-month package</span>
                <span>CA${selectedPackage.total_price.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>Monthly payments</span>
                <span>CA${selectedPackage.monthly_payment} × {selectedPackage.duration_months}</span>
              </div>
              <div className="flex justify-between text-xs text-green-600">
                <span>✓ All utilities included</span>
                <span>✓ Weekly cleaning service</span>
              </div>
              <Separator className="my-3" />
              <div className="flex justify-between font-semibold text-base">
                <span>Total</span>
                <span>CA${totals.total.toLocaleString()}</span>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Pay in {selectedPackage.duration_months} monthly installments
              </p>
            </div>
          ) : (
            <div className="space-y-3 text-sm" data-testid="booking-price-breakdown">
              <div className="flex justify-between">
                <span className="underline" data-testid="price-per-night-label">
                  CA${totals.pricePerNight} × {totals.nights} {totals.nights === 1 ? 'night' : 'nights'}
                </span>
                <span data-testid="accommodation-subtotal">CA${totals.accommodationSubtotal.toFixed(2)}</span>
              </div>
              {totals.discountAmount > 0 && (
                <div className="flex justify-between text-green-600 font-medium">
                  <span className="flex items-center gap-1">
                    <span className="text-sm">💰 {totals.discountLabel} ({totals.discountPercentage}% off)</span>
                  </span>
                  <span>-CA${totals.discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="underline">Cleaning fee</span>
                <span>CA${totals.cleaningFee.toFixed(2)}</span>
              </div>
              {totals.managementFee > 0 && (
                <div className="flex justify-between">
                  <span className="underline">Management fee</span>
                  <span>CA${totals.managementFee.toFixed(2)}</span>
                </div>
              )}
              {totals.petFee > 0 && (
                <div className="flex justify-between">
                  <span className="underline">Pet fee</span>
                  <span>CA${totals.petFee.toFixed(2)}</span>
                </div>
              )}
              {totals.communicationFee > 0 && (
                <div className="flex justify-between">
                  <span className="underline">Communication fee</span>
                  <span>CA${totals.communicationFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="underline">Provincial tax (14.975%)</span>
                <span>CA${totals.provincialTax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="underline">City tax (3.5%)</span>
                <span>CA${totals.cityTax.toFixed(2)}</span>
              </div>

              {/* Promo code — slot in before damage deposit / total */}
              <Separator className="my-3" />
              <PromoCodeInput
                // Validate the code against the accommodation subtotal only —
                // discounts apply to the stay price, never to cleaning fees,
                // communication fees, or taxes. Mirrors the server-side
                // `create_payment_intent` calculation so the displayed savings
                // and the actual charge can never drift apart.
                bookingAmount={totals.accommodationSubtotal}
                propertyId={property.id || property._id}
                userEmail={undefined}
                onCodeApplied={(code) => setAppliedPromo(code)}
              />
              {appliedPromo && (
                <div className="flex justify-between text-green-700 font-medium" data-testid="promo-discount-row">
                  <span className="flex items-center gap-1">
                    🎟 Promo {appliedPromo.promo_code}
                  </span>
                  <span>-CA${Number(appliedPromo.savings).toFixed(2)}</span>
                </div>
              )}

              {totals.damageDeposit > 0 && (
                <>
                  <Separator className="my-3" />
                  <div className="flex justify-between text-blue-600 font-medium">
                    <span className="flex items-center gap-1">
                      Damage deposit
                      <span className="text-xs font-normal">(refundable)</span>
                    </span>
                    <span>CA${totals.damageDeposit.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-500 -mt-1">
                    Authorized as a security hold on your card — not charged unless damage is reported.
                  </p>
                </>
              )}
              {/* Parking add-on line (only shows when the guest opted in
                  and a slot is available). */}
              {parkingAddTotal > 0 && (
                <div
                  className="flex justify-between"
                  data-testid="parking-breakdown-row"
                >
                  <span className="underline">
                    Parking · {parkingSelection.slot?.label || 'Reserved spot'}
                  </span>
                  <span>CA${parkingAddTotal.toFixed(2)}</span>
                </div>
              )}

              <Separator className="my-3" />
              <div className="flex justify-between font-semibold text-base" data-testid="booking-total-row">
                <span>Total</span>
                <span data-testid="booking-total">CA${(totals.total + parkingAddTotal).toFixed(2)}</span>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Payment Modal - Handles both instant and request booking */}
      {/* finalTotal already accounts for parking add-ons AND any applied promo
          savings — the promo discount is folded into `totals.total` via
          usePropertyPricing(..., promoDiscount) so taxes are computed on the
          post-discount accommodation, matching the backend exactly. Do NOT
          subtract appliedPromo.savings again here — that double-subtraction
          was the source of the customer-frontend vs Stripe-charge mismatch. */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        amount={(totals?.total || 0) + parkingAddTotal}
        totalPrice={(totals?.total || 0) + parkingAddTotal}
        propertyId={property.id || property._id}
        propertyTitle={property.title}
        property={property}
        checkIn={checkInDate}
        checkOut={checkOutDate}
        guests={guests}
        onPaymentSuccess={handlePaymentSuccess}
        bookingType={bookingType}
        roomNumber={selectedRoom?.room_number}
        roomName={selectedRoom?.name}
        isPackage={!!selectedPackage}
        packageDuration={selectedPackage?.duration_months}
        bookingMode={property.booking_mode || 'instant'}
        pricePerNight={totals?.pricePerNight}
        accommodationSubtotal={totals?.accommodationSubtotal}
        promoCode={appliedPromo?.promo_code}
        promoSavings={appliedPromo?.savings || 0}
        discountAmount={totals?.discountAmount || 0}
        parkingRequested={parkingSelection.requested && parkingSelection.available === true}
        parkingCarSize={parkingSelection.carSize}
        parkingSlot={parkingSelection.slot}
        parkingTotal={parkingAddTotal}
      />
      
      {/* Request to Book Modal */}
      <RequestToBookModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSubmit={handleRequestSubmit}
        property={property}
        checkIn={checkInDate}
        checkOut={checkOutDate}
        guests={guests}
        totalPrice={totals?.total || 0}
        bookingType={bookingType}
        selectedRoom={selectedRoom}
        pricePerNight={totals?.pricePerNight}
        accommodationSubtotal={totals?.accommodationSubtotal}
        discountAmount={totals?.discountAmount || 0}
        promoCode={appliedPromo?.promo_code}
        promoSavings={appliedPromo?.savings || 0}
      />

      {/* Stripe Identity verification gate — first-time bookers must verify */}
      <IdVerificationRequiredModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerified={() => {
          setShowVerificationModal(false);
          // Resume the booking flow the user originally intended
          openBookingModal();
        }}
      />

      {/* Account Health hard-gate appeal */}
      <AccountAppealModal
        isOpen={showAppealModal}
        onClose={() => setShowAppealModal(false)}
      />
    </Card>
  );
};

export default BookingWidget;